import { useUser } from '@clerk/clerk-react'
import type { ReactNode } from 'react'

import { AgenciesPageHeader } from '@/components/agencies/agencies-page-header.molecule'
import { ConfirmDialog } from '@/components/molecules/confirm-dialog.molecule'
import { ScrollArea } from '@/components/ui/scroll-area'

import { AgenciesGridContent } from './agencies-grid-content.view'
import { PromptTemplatesListView } from './prompt-templates-list.view'
import { useAgenciesFilter } from './use-agencies-filter.hook'
import { useAgenciesNavigation } from './use-agencies-navigation.hook'
import { useAgencyDeleteConfirm } from './use-agency-delete-confirm.hook'
import { useAgencyProAccess } from './use-agency-pro-access.hook'

const buildDeleteDescription = (name?: string): string =>
  `Are you sure you want to delete "${name ?? ''}"? This action cannot be undone.`

export const AgenciesView = (): ReactNode => {
  const { user } = useUser()
  const filter = useAgenciesFilter()
  const navigation = useAgenciesNavigation()
  const deleteConfirm = useAgencyDeleteConfirm()
  const { disabledTabList, handleUpgradeClick } = useAgencyProAccess(filter.activeTab)

  const isTemplatesTab = filter.activeTab === 'templates'
  const handleDialogClose = (isOpen: boolean): void => {
    if (!isOpen) deleteConfirm.handleDeleteCancel()
  }

  return (
    <div className="agencies-wrapper flex h-full flex-col">
      <div className="agencies-content flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-6">
            <AgenciesPageHeader
              activeTab={filter.activeTab}
              onTabChange={filter.handleTabChange}
              disabledTabList={disabledTabList}
              onUpgradeClick={handleUpgradeClick}
            />
            {isTemplatesTab ? (
              <PromptTemplatesListView />
            ) : (
              <AgenciesGridContent
                currentUserId={user?.id}
                filter={filter}
                navigation={navigation}
                deleteConfirm={deleteConfirm}
              />
            )}
          </div>
        </ScrollArea>
      </div>
      <ConfirmDialog
        isOpen={deleteConfirm.agencyToDelete !== null}
        onOpenChange={handleDialogClose}
        title="Delete Agency"
        description={buildDeleteDescription(deleteConfirm.agencyToDelete?.name)}
        confirmLabel="Delete"
        onConfirm={deleteConfirm.handleDeleteConfirm}
        onCancel={deleteConfirm.handleDeleteCancel}
        variant="destructive"
      />
    </div>
  )
}
